<template>
  <div class="join-widget py-8 px-4 md:px-8">
    <!-- Top Text from Strapi -->
    <div
      v-if="renderedTopText"
      class="disclaimer-text text-center text-[12px] leading-[16px] tracking-[-0.01em] text-[#979797] font-normal max-w-[1280px] mx-auto mb-6 md:mb-8 px-4"
      v-html="renderedTopText"
    ></div>

    <!-- Main Wisteria Section -->
    <div class="relative flex flex-col items-center max-w-[1280px] mx-auto">
      <div
        class="join-widget__main-section bg-[#F4E7FF] w-full rounded-[24px] flex flex-col items-start px-6 md:px-[48px] pt-8 md:pt-[48px] pb-28 md:pb-[120px] relative overflow-visible"
      >
        <!-- Mascot Image -->
        <div
          v-if="content.imgMascot?.url"
          class="absolute mascot-position"
        >
          <img
            :src="useStrapiMedia(content.imgMascot?.url)"
            :alt="content.imgMascot?.alternativeText || 'OnePass Mascot'"
            class="w-[180px] h-auto object-contain"
          />
        </div>

        <!-- Section Title -->
        <h2
          class="text-[#3A016A] text-[22px] md:text-[32px] font-bold text-center w-full mb-6 md:mb-8 leading-[28px] md:leading-[40px] tracking-[-0.01em]"
        >
          {{ content.sectionTitle }}
        </h2>

        <!-- Benefit Tiles -->
        <div
          class="benefit-tiles-grid flex flex-col sm:flex-row flex-wrap justify-center gap-4 md:gap-6 w-full"
        >
          <div
            v-for="(tile, index) in content.benefitTiles"
            :key="index"
            class="benefit-card bg-white rounded-[12px] p-4 md:p-6 flex flex-col items-start border border-[#EEEEE9] gap-2 md:gap-4"
          >
            <!-- Icon/Logo -->
            <div class="h-[32px]">
              <img
                v-if="tile.image?.url"
                :src="useStrapiMedia(tile.image?.url)"
                :alt="tile.heading || tile.content"
                class="h-full w-auto object-contain"
              />
            </div>
            
            <!-- Heading -->
            <h3 class="text-[#333333] text-[16px] md:text-[18px] font-bold leading-[24px] md:leading-[26px]">
              {{ tile.heading }}
            </h3>
            
            <!-- Content -->
            <p class="text-[#333333] text-[14px] md:text-[16px] font-normal leading-[22px] md:leading-[24px] flex-grow">
              {{ tile.content }}
            </p>
            
            <!-- Learn More Link -->
            <a
              v-if="tile.learnMoreLink"
              :href="tile.learnMoreLink"
              target="_blank"
              rel="noopener noreferrer"
              class="learn-more-link text-[#333333] text-[14px] md:text-[16px] font-semibold leading-[22px] md:leading-[24px] border-b border-[#333333] hover:text-primary hover:border-primary transition-colors mt-auto"
            >
              {{ tile.learnMoreText || 'Learn more' }}
            </a>
          </div>
        </div>
      </div>


      <!-- Download App Box -->
      <div
        class="download-app-box flex bg-[#3A016A] py-6 px-6 md:py-8 md:px-12 rounded-[12px] w-[94%] md:w-[762px] mx-auto absolute bottom-[-200px] md:bottom-[-90px] flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-5"
      >
        <div class="flex flex-col items-start flex-shrink-0">
          <h3
            class="text-white text-[18px] md:text-[20px] font-bold leading-[24px] md:leading-[28px]"
          >
            {{ content.appTitle }}
          </h3>
          <p class="text-white text-[14px] md:text-[16px] font-normal leading-[22px] md:leading-[24px] mt-3">
            {{ content.appSubTitle }}
          </p>
        </div>

        <!-- QR Code -->
        <div class="qr-code-placeholder flex-shrink-0 w-[100px] h-[100px] md:w-[117px] md:h-[117px] bg-white rounded-[4px] flex items-center justify-center self-center md:self-auto overflow-hidden">
          <img
            v-if="qrCodeImageUrl"
            :src="useStrapiMedia(qrCodeImageUrl)"
            alt="Download QR Code"
            class="w-full h-full object-contain"
          />
          <span v-else class="text-[12px] md:text-[14px] text-black text-center leading-tight px-2"></span>
        </div>
      </div>
    </div>

    <!-- Spacer for absolute positioned download box -->
    <div class="h-[220px] md:h-28"></div>

    <!-- Steps and App Screenshot Section - Purple Background -->
    <div
      v-if="content.stepsSection"
      class="steps-section-wrapper w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mt-16"
    >
      <div class="steps-section-container relative overflow-hidden" style="background: linear-gradient(180deg, #9900F1 0%, #7A00C1 100%);">
        <!-- Purple diagonal pattern overlay -->
        <div class="steps-pattern-overlay"></div>
        
        <div class="steps-inner-container relative z-10 flex flex-col lg:flex-row justify-center items-center gap-6 px-6 lg:px-12 xl:px-16 max-w-[954px] mx-auto">
          <!-- Left side - Steps content (from Strapi) -->
          <div class="steps-content flex flex-col items-start gap-5 w-full lg:w-[465px] lg:flex-shrink-0 py-10 lg:py-16">
            <div class="steps-rendered-content" v-html="renderedStepsSection"></div>

            <!-- CTA Button -->
            <button
              v-if="content.buttonText"
              class="steps-cta-button"
              @click="navigateToActivate"
            >
              {{ content.buttonText }}
            </button>

            <!-- Bottom Link -->
            <div v-if="content.bottomLink" class="bottom-link text-white text-[16px] leading-[24px]" v-html="renderedBottomLink"></div>
          </div>
          
          <!-- Right side - Phone Image -->
          <div v-if="content.appScreenshot?.url" class="phone-container">
            <img
              :src="useStrapiMedia(content.appScreenshot?.url)"
              alt="App Screenshot"
              class="phone-image"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import MarkdownIt from "markdown-it";
import { useRouter } from "vue-router";
import { useMainStore } from "@/stores/main";
import { storeToRefs } from "pinia";

const router = useRouter();
const md = new MarkdownIt({ html: true, linkify: true, breaks: true });
const mainStore = useMainStore();
const { globalSettings } = storeToRefs(mainStore);

const qrCodeImageUrl = computed(() => globalSettings.value?.qrCodeImage?.url || null);

const props = defineProps({
  content: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});

const renderedTopText = computed(() => {
  if (!props.content.topText) return "";
  return md.render(props.content.topText);
});

const renderedStepsSection = computed(() => {
  let cleanContent =
    props.content.stepsSection
      ?.split("\n")
      .map((line: string) => line.trim())
      .join("\n") || "";

  return md.render(
    cleanContent.replace(
      /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
      (_: string, phone: string) => `[${phone}](tel:${phone.replace(/\s+/g, "")})`
    )
  );
});

const renderedBottomLink = computed(() =>
  md.render(props.content.bottomLink || "")
);

const navigateToActivate = () => {
  router.push("/sim-activation");
};
</script>

<style scoped lang="scss">
.join-widget__main-section {
  position: relative;
}

.mascot-position {
  position: absolute;
  top: -40px;
  right: -20px;
  z-index: 10;
}

@media (max-width: 767px) {
  .mascot-position {
    top: -55px;
    right: -30px;
  }

  .mascot-position img {
    width: 100px !important;
  }
}

.benefit-card {
  min-height: 200px;
  flex: 1 1 100%;
}

@media (min-width: 640px) {
  .benefit-card {
    flex: 1 1 calc(50% - 12px);
    max-width: calc(50% - 12px);
  }
}

@media (min-width: 768px) {
  .benefit-card {
    min-height: 244px;
  }
}

@media (min-width: 1024px) {
  .benefit-card {
    flex: 1 1 0%;
    max-width: none;
  }
}

.steps-section-container {
  position: relative;
}

.steps-inner-container {
  min-height: 484px;
}

.phone-container {
  position: relative;
  align-self: stretch;
  flex: 1 1 0%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  overflow: hidden;
}

.steps-pattern-overlay {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  background-image: url('/images/onepass-repeat-pattern.svg');
  background-repeat: no-repeat;
  background-position: right center;
  background-size: auto 100%;
  opacity: 0.5;
}

.phone-image {
  object-fit: contain;
  filter: drop-shadow(0px 4px 24px rgba(0, 0, 0, 0.25));
}

/* CTA Button - Figma: 210x48, padding 12px 24px, border-radius 24px */
.steps-cta-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 24px;
  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  border-radius: 24px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #9900F1;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f0f0f0;
  }
}

/* Steps rendered content - styles for Strapi HTML */
.steps-rendered-content {
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* Main title: h2:first-child or h1 */
  :deep(h1),
  :deep(h2:first-child) {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 28px;
    line-height: 34px;
    letter-spacing: -0.01em;
    color: #FFFFFF;
    margin: 0 0 12px 0;
    white-space: nowrap;

    @media (min-width: 768px) {
      font-size: 40px;
      line-height: 48px;
    }
  }

  /* Sub-headings (step titles) */
  :deep(h2) {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: #FFFFFF;
    margin: 0;
  }

  :deep(h3) {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: #FFFFFF;
    margin: 0;
  }

  /* Paragraphs */
  :deep(p) {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #FFFFFF;
    margin: 0;
  }

  :deep(ol) {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  :deep(li) {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: #FFFFFF;
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(a) {
    color: #FFFFFF;
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
}

.bottom-link {
  :deep(a) {
    color: #FFFFFF;
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
}

/* Mobile styles (< 1024px) */
@media (max-width: 1023px) {
  
  .steps-inner-container {
    min-height: auto;
  }
  
  .phone-container {
    align-self: center;
    flex: none;
  }

  .phone-image {
    width: 220px;
  }
  
  .steps-pattern-overlay {
    background-size: auto 80%;
    background-position: right bottom;
    opacity: 0.3;
  }
  
  .steps-section-container {
    overflow: hidden;
  }
}

/* Desktop styles (>= 1024px) */
@media (min-width: 1024px) {
  .steps-section-container {
    overflow: visible;
  }
}
</style>
